import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Loader2, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, ArrowLeft, FileText, ClipboardCheck } from 'lucide-react';
import SafetyReportModal from '../../components/SafetyReportModal';
import { useAlert } from '../../context/AlertContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExecutiveDashboard = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useAlert();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  useEffect(() => {
    fetchDashboard();
    // Refresh every 30 seconds
    const interval = setInterval(fetchDashboard, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/executive`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard');
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    setShowReportModal(true);
    
    // Give React time to render the modal before starting the long API call
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/safety-report`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 60000  // 60 second timeout for AI processing
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `Saarv_Organizational_Safety_Report_${today}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // Wait to show completion
      await new Promise(resolve => setTimeout(resolve, 1000));
      setShowReportModal(false);


      // Show success message
      setTimeout(() => {
        showSuccess('Organizational Safety Report downloaded successfully! The report contains: Executive summary, Key performance indicators, Red flags and green flags, SSEM maturity assessment, Strategic recommendations, Future roadmap');
      }, 500);
    } catch (error) {
      console.error('Failed to generate report:', error);
      setShowReportModal(false);
      if (error.response?.status === 401) {
        showError('Session expired. Please login again.');
        window.location.href = '/login';
      } else {
        showError('Failed to generate safety report. Please try again.');
      }
    } finally{
      setGeneratingReport(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const { pss, leading_indicators, ssem_overall_level, ltifr_current, ai_summary, anomalies, top_risks, ptw_metrics } = dashboard;

  const getPSSColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getSeverityColor = (severity) => {
    const colors = {
      Low: 'bg-green-100 text-green-800',
      Medium: 'bg-yellow-100 text-yellow-800',
      High: 'bg-orange-100 text-orange-800',
      Critical: 'bg-red-100 text-red-800'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="executive-dashboard">
      {/* Header with Back Button */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <Button 
              variant="outline" 
              onClick={() => navigate('/')} 
              className="mb-4"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">Executive Safety Dashboard</h1>
            <p className="text-gray-600 mt-1">Real-time safety performance and leading indicators</p>
          </div>
          <Button
            onClick={handleGenerateReport}
            disabled={generatingReport}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all"
          >
            <FileText className="w-5 h-5 mr-2" />
            {generatingReport ? 'Generating...' : 'Generate Safety Report'}
          </Button>
        </div>
      </div>

      {/* Top Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {/* Predictive Safety Score */}
        <Card className={`${getPSSColor(pss.score)} border-2`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Predictive Safety Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{pss.score}</div>
            <div className="text-sm mt-1">{pss.risk_level}</div>
            <div className="flex items-center mt-2 text-xs">
              {pss.trend === 'Improving' ? (
                <TrendingUp className="w-4 h-4 mr-1" />
              ) : (
                <TrendingDown className="w-4 h-4 mr-1" />
              )}
              {pss.trend}
            </div>
          </CardContent>
        </Card>

        {/* LTIFR */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">LTIFR (12-Month)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{leading_indicators.ltifr_12m_rolling}</div>
            <div className="text-sm text-gray-600 mt-1">Lost Time Injury Frequency Rate</div>
            <div className="text-xs text-gray-500 mt-2">Current Month: {ltifr_current}</div>
          </CardContent>
        </Card>

        {/* High-Risk Hazards */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">High-Risk Hazards Open</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">{leading_indicators.high_risk_hazards_open}</div>
            <div className="text-sm text-gray-600 mt-1">Requiring immediate attention</div>
          </CardContent>
        </Card>

        {/* SSEM Level */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">SSEM Maturity Level</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">Level {ssem_overall_level}</div>
            <div className="text-sm text-gray-600 mt-1">Safety Excellence Model</div>
          </CardContent>
        </Card>
      </div>

      {/* AI Summary */}
      <Card className="mb-6 border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center text-blue-900">
            <CheckCircle className="w-5 h-5 mr-2" />
            AI Safety Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-blue-900 leading-relaxed">{ai_summary}</p>
        </CardContent>
      </Card>

      {/* Leading Indicators Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Observations & Reporting</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Near Miss Rate (per 100)</span>
              <span className="font-semibold">{leading_indicators.near_miss_rate}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Unsafe Acts Reported</span>
              <span className="font-semibold">{leading_indicators.unsafe_acts_reported}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Unsafe Conditions</span>
              <span className="font-semibold">{leading_indicators.unsafe_conditions_reported}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">CAPA Performance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Closure Rate</span>
              <span className="font-semibold">{leading_indicators.capa_closure_rate}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">High-Risk Overdue</span>
              <span className="font-semibold text-red-600">{leading_indicators.high_risk_capas_overdue}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Effectiveness Score</span>
              <span className="font-semibold">{leading_indicators.capa_effectiveness_score}%</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Compliance & Audit</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Compliance Score</span>
              <span className="font-semibold">{leading_indicators.compliance_score_monthly}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Audit Compliance</span>
              <span className="font-semibold">{leading_indicators.audit_compliance_score}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Competence Score</span>
              <span className="font-semibold">{leading_indicators.org_competence_score}%</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* PTW Metrics */}
      {ptw_metrics && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center">
                <ClipboardCheck className="w-4 h-4 mr-2 text-blue-600" />
                PTW Issued
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{ptw_metrics.ptw_issued}</div>
              <div className="text-sm text-gray-600 mt-1">Total permits issued</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center">
                <ClipboardCheck className="w-4 h-4 mr-2 text-green-600" />
                Open PTW
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{ptw_metrics.open_ptw}</div>
              <div className="text-sm text-gray-600 mt-1">Currently active permits</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center">
                <ClipboardCheck className="w-4 h-4 mr-2 text-orange-600" />
                Extended PTW
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">{ptw_metrics.extended_ptw}</div>
              <div className="text-sm text-gray-600 mt-1">Permits with approved extensions</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Anomalies & Top Risks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Anomalies */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-orange-700">
              <AlertTriangle className="w-5 h-5 mr-2" />
              AI-Detected Anomalies
            </CardTitle>
          </CardHeader>
          <CardContent>
            {anomalies.length === 0 ? (
              <p className="text-gray-500 text-sm">No anomalies detected</p>
            ) : (
              <div className="space-y-3">
                {anomalies.map((anomaly, idx) => (
                  <div key={idx} className="border-l-4 border-orange-500 pl-3 py-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{anomaly.description}</span>
                      <span className={`text-xs px-2 py-1 rounded ${getSeverityColor(anomaly.severity)}`}>
                        {anomaly.severity}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">{anomaly.recommendation}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Risks */}
        <Card>
          <CardHeader>
            <CardTitle className="text-red-700">Top Open Risks</CardTitle>
          </CardHeader>
          <CardContent>
            {top_risks.length === 0 ? (
              <p className="text-gray-500 text-sm">No high-risk hazards open</p>
            ) : (
              <div className="space-y-2">
                {top_risks.slice(0, 5).map((risk, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{risk.title}</p>
                      <p className="text-xs text-gray-600">{risk.location || 'Location not specified'}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${getSeverityColor(risk.severity)}`}>
                      {risk.severity}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Safety Report Generation Modal */}
      <SafetyReportModal 
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
      />
    </div>
  );
};

export default ExecutiveDashboard;
